<template>
  <div
    class="tw:mx-auto tw:bg-white tw:rounded-lg tw:w-full tw:p-4 tw:border tw:border-gray-300"
    role="application"
    aria-label="Date picker"
  >
    <div class="tw:flex tw:items-center tw:justify-between tw:mb-6">
      <button
        v-if="canGoToPreviousMonth"
        @click="previousMonth"
        aria-label="Previous month"
        class="tw:p-2 tw:hover:bg-gray-100 tw:rounded-lg tw:transition-colors tw:-ml-2 tw:hover:cursor-pointer"
      >
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M6 1L1 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
          <path d="M6 11L1 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
      <div v-else class="tw:w-[39px]"></div>

      <h2 class="tw:text-gray-900 tw:text-center tw:font-bold">
        {{ `${$t(currentMonthName)} ${displayYear}` }}
      </h2>

      <button
        v-if="canGoToNextMonth"
        @click="nextMonth"
        aria-label="Next month"
        class="tw:p-2 tw:hover:bg-gray-100 tw:rounded-lg tw:transition-colors tw:-mr-2 tw:hover:cursor-pointer"
      >
        <svg width="7" height="12" viewBox="0 0 7 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
          <path d="M1 11L6 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
          <path d="M1 1L6 6" stroke="#0A1121" stroke-width="2" stroke-linecap="round" />
        </svg>
      </button>
      <div v-else class="tw:w-[39px]"></div>
    </div>

    <div role="grid">
      <div class="tw:grid tw:grid-cols-7 tw:gap-1 tw:mb-2 tw:bg-gray-200 tw:py-2 tw:mx-0 tw:px-4 tw:lg:px-6" role="row">
        <div
          v-for="day in daysOfWeek"
          :key="'dow-' + day.value"
          role="columnheader"
          class="tw:text-center tw:text-xs tw:text-gray-800"
          :class="{ 'tw:font-bold': day.value === 'Sat' || day.value === 'Sun' }"
          :aria-label="day.value"
        >
          {{ $t(day.label) }}
        </div>
      </div>

      <div class="tw:grid tw:grid-cols-7 tw:px-2 tw:lg:px-4" role="rowgroup">
        <div
          v-for="blank in startingDayOfWeek"
          :key="'blank-' + blank"
          class="tw:w-[44px] tw:h-[44px] tw:relative"
          role="gridcell"
          aria-hidden="true"
        />

        <div v-for="day in daysInMonth" :key="'day-' + day" class="tw:w-[44px] tw:h-[44px] tw:relative" role="gridcell">
          <button
            @click="selectDay(day)"
            @keydown.enter="selectDay(day)"
            @keydown.space.prevent="selectDay(day)"
            :disabled="!isDateSelectable(day)"
            :aria-disabled="!isDateSelectable(day)"
            :aria-label="getDateAriaLabel(day)"
            :aria-pressed="isSelected(day)"
            class="tw:absolute tw:inset-0 tw:flex tw:items-center tw:justify-center tw:rounded-full tw:text-sm"
            :class="getDayClasses(day)"
          >
            {{ day }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed } from 'vue'

const props = defineProps({
  resumeDate: Object,
})

const emit = defineEmits(['setResumeDate'])

const today = new Date()
today.setHours(0, 0, 0, 0)
const todayMonth = today.getMonth()
const todayYear = today.getFullYear()

const displayMonth = ref(props.resumeDate?.month ?? todayMonth)
const displayYear = ref(props.resumeDate?.year ?? todayYear)
const selectedDay = ref(props.resumeDate?.day || null)
const selectedMonth = ref(props.resumeDate?.month ?? null)
const selectedYear = ref(props.resumeDate?.year ?? null)
const hasUserSelected = ref(false)

const monthNames = [
  'month_1',
  'month_2',
  'month_3',
  'month_4',
  'month_5',
  'month_6',
  'month_7',
  'month_8',
  'month_9',
  'month_10',
  'month_11',
  'month_12',
]

const daysOfWeek = [
  { value: 'Mon', label: 'smart_boost_calendar_monday' },
  { value: 'Tue', label: 'smart_boost_calendar_tuesday' },
  { value: 'Wed', label: 'smart_boost_calendar_wednesday' },
  { value: 'Thu', label: 'smart_boost_calendar_thursday' },
  { value: 'Fri', label: 'smart_boost_calendar_friday' },
  { value: 'Sat', label: 'smart_boost_calendar_saturday' },
  { value: 'Sun', label: 'smart_boost_calendar_sunday' },
]

const currentMonthName = computed(() => {
  return monthNames[displayMonth.value]
})

const daysInMonth = computed(() => {
  return new Date(displayYear.value, displayMonth.value + 1, 0).getDate()
})

const startingDayOfWeek = computed(() => {
  const day = new Date(displayYear.value, displayMonth.value, 1).getDay()
  return day === 0 ? 6 : day - 1
})

const hasSelectableDatesInPreviousMonth = () => {
  const prevMonth = displayMonth.value === 0 ? 11 : displayMonth.value - 1
  const prevYear = displayMonth.value === 0 ? displayYear.value - 1 : displayYear.value

  const daysInPrevMonth = new Date(prevYear, prevMonth + 1, 0).getDate()

  for (let day = 1; day <= daysInPrevMonth; day++) {
    const date = new Date(prevYear, prevMonth, day)
    date.setHours(0, 0, 0, 0)
    if (date >= today) {
      return true
    }
  }
  return false
}

const canGoToPreviousMonth = computed(() => {
  return hasSelectableDatesInPreviousMonth()
})

const canGoToNextMonth = computed(() => {
  const maxDate = new Date(today)
  maxDate.setMonth(maxDate.getMonth() + 6)

  const nextMonthDate = new Date(displayYear.value, displayMonth.value + 1, 1)
  nextMonthDate.setHours(0, 0, 0, 0)

  return nextMonthDate <= maxDate
})

const isPastDate = day => {
  const dateToCheck = new Date(displayYear.value, displayMonth.value, day)
  dateToCheck.setHours(0, 0, 0, 0)
  return dateToCheck <= today
}

const isTooFarInFuture = day => {
  const maxDate = new Date(today)
  maxDate.setMonth(maxDate.getMonth() + 6)

  const dateToCheck = new Date(displayYear.value, displayMonth.value, day)
  dateToCheck.setHours(0, 0, 0, 0)
  return dateToCheck > maxDate
}

const isDateSelectable = day => {
  return !isPastDate(day) && !isTooFarInFuture(day)
}

const isSelected = day => {
  return (
    selectedDay.value === day && selectedMonth.value === displayMonth.value && selectedYear.value === displayYear.value
  )
}

const getDayClasses = day => {
  const past = isPastDate(day)
  const tooFar = isTooFarInFuture(day)
  const selected = isSelected(day)

  if (past || tooFar) {
    return 'tw:text-gray-300 tw:cursor-not-allowed hover:tw:bg-transparent'
  }

  if (selected) {
    if (!hasUserSelected.value) {
      return 'tw:font-bold tw:border-2 tw:border-blue-700 tw:text-blue-700 tw:cursor-pointer tw:hover:bg-blue-100'
    }
    return 'tw:font-bold tw:bg-blue-700 tw:text-white tw:hover:bg-blue-700 tw:cursor-pointer'
  }

  return 'tw:text-gray-700 tw:hover:bg-blue-200 tw:cursor-pointer'
}

const selectDay = day => {
  if (!isDateSelectable(day)) return

  selectedDay.value = day
  selectedMonth.value = displayMonth.value
  selectedYear.value = displayYear.value
  hasUserSelected.value = true

  emit('setResumeDate', {
    day,
    month: displayMonth.value,
    year: displayYear.value,
    fullDate: new Date(displayYear.value, displayMonth.value, day),
  })
}

const previousMonth = () => {
  if (!canGoToPreviousMonth.value) return

  if (displayMonth.value === 0) {
    displayMonth.value = 11
    displayYear.value -= 1
  } else {
    displayMonth.value -= 1
  }
}

const nextMonth = () => {
  if (!canGoToNextMonth.value) return

  if (displayMonth.value === 11) {
    displayMonth.value = 0
    displayYear.value += 1
  } else {
    displayMonth.value += 1
  }
}

const getDateAriaLabel = day => {
  const date = new Date(displayYear.value, displayMonth.value, day)
  const dateString = date.toLocaleDateString(undefined, {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })

  const selected = isSelected(day)
  const past = isPastDate(day)
  const tooFar = isTooFarInFuture(day)

  let label = dateString
  if (selected) label += ', selected'
  if (past) label += ', past date, not available'
  if (tooFar) label += ', too far in future, not available'

  return label
}
</script>
